import { apiFetchBlob, apiUpload, apiFetch } from '@/api/client'

/**
 * A business card photographed at a stand, hanging off the person who handed it over.
 *
 * Served by the API rather than as a static file, and on purpose: the lead's visibility rule
 * governs the card exactly as it governs the lead. A path under a web root would hand every card
 * in the system to anybody who could guess a URL, and a photographed card is personal data.
 */
export type BusinessCard = {
  id: string
  contentType: string
  sizeBytes: number
  originalFilename: string | null
  /** False when the image could not be read — the card is kept, only the preview is missing. */
  hasThumbnail: boolean
  uploadedBy: string
  uploadedAt: string
  sortIndex: number
}

function base(leadId: string, contactId: string): string {
  return `/api/leads/${encodeURIComponent(leadId)}/contacts/${encodeURIComponent(contactId)}/business-cards`
}

export function listBusinessCards(
  leadId: string,
  contactId: string,
  signal?: AbortSignal,
): Promise<BusinessCard[]> {
  return apiFetch<BusinessCard[]>(base(leadId, contactId), { signal })
}

/**
 * JPEG or PNG. The backend decides by what the bytes begin with, so a HEIC renamed to `.jpg` is
 * refused with `FILE_UNSUPPORTED_TYPE` — an iPhone produces HEIC unless told otherwise, and the JVM
 * cannot read it.
 */
export function uploadBusinessCard(
  leadId: string,
  contactId: string,
  file: File,
): Promise<BusinessCard> {
  return apiUpload<BusinessCard>(base(leadId, contactId), file)
}

export function deleteBusinessCard(
  leadId: string,
  contactId: string,
  cardId: string,
): Promise<void> {
  return apiFetch<void>(`${base(leadId, contactId)}/${encodeURIComponent(cardId)}`, {
    method: 'DELETE',
  })
}

/** The bytes, for an object URL. `THUMBNAIL` is the generated preview. */
export function fetchBusinessCardImage(
  leadId: string,
  contactId: string,
  cardId: string,
  variant: 'ORIGINAL' | 'THUMBNAIL',
  signal?: AbortSignal,
): Promise<Blob> {
  return apiFetchBlob(
    `${base(leadId, contactId)}/${encodeURIComponent(cardId)}?variant=${variant}`,
    { signal },
  )
}

/** What the backend accepts, mirrored so the file picker offers only those. */
export const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png'

/**
 * Front and back. Mirrors `BusinessCardService.MAX_CARDS_PER_CONTACT`, so the strip can stop
 * offering an upload the backend would refuse — the refusal still stands, this only keeps somebody
 * from meeting it.
 */
export const MAX_CARDS_PER_CONTACT = 2
